/**
 * Общие поля мастера подключения (KAN-601): метка, прокси, флаги.
 * Флаги выбираются ДО входа: после подключения их не меняют сутки.
 * KAN-655: прокси теми же полями, что в диалоге смены прокси (ProxyFields),
 * строка провайдера, вставленная в "Адрес", раскладывается по полям.
 * KAN-713: над полями выбор "Прокси из справочника"; выбранный скрывает поля.
 * KAN-722: метка выбирается префиксом (LabelPrefixPicker); оба флага по
 * умолчанию выключены: аккаунт включается и пишет первым только по выбору.
 * KAN-740: по выбранному префиксу метки подставляется прокси справочника
 * (useProxyAutoPick); ручной выбор прокси подстановку отключает.
 */
import { LabelPrefixPicker } from '@/components/telegram/LabelPrefixPicker';
import { ProxyDirectoryPicker } from '@/components/telegram/ProxyDirectoryPicker';
import { ProxyFields } from '@/components/telegram/ProxyFields';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useProxyAutoPick } from '@/hooks/useProxyAutoPick';
import type { CommonValues } from '@/lib/telegramConnect';

export const FLAGS_HINT =
  'По умолчанию аккаунт включён и первым не пишет. Флаги выбираются до входа: после подключения не меняйте их сутки.';

interface Props {
  values: CommonValues;
  onChange: (patch: Partial<CommonValues>) => void;
  /** Ошибка проверки прокси (validateOptionalProxy), показывается под полями. */
  proxyError?: string | null;
  disabled?: boolean;
  /** KAN-740: подставлять прокси по префиксу метки (выключено при переподключении). */
  autoProxy?: boolean;
}

export function ConnectCommonFields({
  values,
  onChange,
  proxyError,
  disabled,
  autoProxy = true,
}: Props): JSX.Element {
  const { hint: proxyHint, markTouched } = useProxyAutoPick({
    values,
    enabled: autoProxy,
    onPick: (proxyId) => onChange({ proxyId }),
  });
  return (
    <div className="space-y-3">
      <LabelPrefixPicker values={values} disabled={disabled} onChange={onChange} />
      <div className="space-y-2">
        <Label>Прокси (необязательно)</Label>
        <ProxyDirectoryPicker
          idPrefix="connect-proxy"
          proxyId={values.proxyId}
          disabled={disabled}
          onChange={(proxyId) => {
            markTouched();
            onChange({ proxyId });
          }}
          manual={
            <ProxyFields
              idPrefix="connect-proxy"
              values={values.proxy}
              disabled={disabled}
              onChange={(patch) => {
                markTouched();
                onChange({ proxy: { ...values.proxy, ...patch } });
              }}
            />
          }
        />
        {proxyHint && (
          <p className="text-xs text-amber-700" data-testid="connect-proxy-auto-hint">
            {proxyHint}
          </p>
        )}
        {proxyError && (
          <p className="text-xs text-red-600" data-testid="connect-proxy-error">
            {proxyError}
          </p>
        )}
      </div>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="connect-enabled">Включить после подключения</Label>
        <Switch
          id="connect-enabled"
          data-testid="connect-enabled-switch"
          checked={values.enabled}
          disabled={disabled}
          onCheckedChange={(enabled) => onChange({ enabled })}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="connect-outreach">Писать первым</Label>
        <Switch
          id="connect-outreach"
          data-testid="connect-outreach-switch"
          checked={values.outreach}
          disabled={disabled}
          onCheckedChange={(outreach) => onChange({ outreach })}
        />
      </div>
      <p className="text-xs text-gray-500" data-testid="connect-flags-hint">
        {FLAGS_HINT}
      </p>
    </div>
  );
}
